import React, { useEffect, useMemo, useState } from "react";

import { ReloadOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Empty,
  Flex,
  message,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";

import { CambiarHora } from "@/pages/conversations/AgentTasksView";
import { errorDeTarea, fechaDeTarea } from "@/pages/conversations/agent-tasks";

import {
  AgentTaskStatus,
  conversationsApi,
  isConversationsConfigured,
  TaskView,
} from "@/services/conversations.api";
import { getErrorMessage } from "@/utilities";

import {
  consultaDeTareas,
  filasDeTareas,
  FILTRO_DE_ESTADO,
  hayOtraPagina,
  TAREAS_POR_PAGINA,
} from "./tareas";

const { Text, Link } = Typography;

interface Props {
  userId: number;
}

/**
 * Las tareas programadas de un asistente (tarea 5.12, D65): lo pendiente, lo que corre, lo hecho y
 * lo fallado, en todas sus conversaciones y en las fichas del historial (D66), con la hora atrasada
 * si el pod no la tomó a tiempo. La lista sale del conversation-service, como el resto de
 * Conversaciones; leerla pide Viewer de Operadores del Store. Cancelar o cambiar la hora de una de
 * conversación se hace en su nota del chat; la de una ficha no tiene otro lugar, y se hace acá.
 */
export const TareasTab: React.FC<Props> = ({ userId }) => {
  const [estado, setEstado] = useState<AgentTaskStatus | null>(null);
  const [pagina, setPagina] = useState(1);
  const [tareas, setTareas] = useState<TaskView[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  const configurado = isConversationsConfigured();

  useEffect(() => {
    if (!configurado) return;
    let vivo = true;
    setCargando(true);
    setError(null);
    conversationsApi
      .listAgentTasksOfAgent(consultaDeTareas(userId, estado, pagina))
      .then((r) => vivo && setTareas(r))
      .catch((e) => {
        if (!vivo) return;
        setTareas([]);
        setError(getErrorMessage(e));
      })
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
    };
  }, [configurado, userId, estado, pagina, recarga]);

  const filas = useMemo(() => filasDeTareas(tareas, new Date()), [tareas]);
  const otra = hayOtraPagina(tareas.length);
  const [cancelando, setCancelando] = useState<string | null>(null);

  /** Un 403 o una tarea que ya corrió se explican; si lo que se ve está viejo, se relee. */
  const avisarError = (e: unknown, accion: "cancelar" | "cambiar") => {
    const { texto, nivel, releer } = errorDeTarea(e, accion);
    message[nivel](texto);
    if (releer) setRecarga((n) => n + 1);
  };

  const cancelar = async (taskId: string) => {
    setCancelando(taskId);
    try {
      await conversationsApi.cancelAgentTaskById(taskId);
      message.success("Tarea cancelada");
      setRecarga((n) => n + 1);
    } catch (e) {
      avisarError(e, "cancelar");
    } finally {
      setCancelando(null);
    }
  };

  /** `true` si quedó: el selector de hora se cierra sólo entonces. */
  const cambiarHora = async (taskId: string, runAt: string) => {
    try {
      const tarea = await conversationsApi.rescheduleAgentTaskById(
        taskId,
        runAt,
      );
      message.success(`Reprogramada para el ${fechaDeTarea(tarea.runAt)}`);
      setRecarga((n) => n + 1);
      return true;
    } catch (e) {
      avisarError(e, "cambiar");
      return false;
    }
  };
  // La lista no trae el total: se cuenta una fila más cuando puede haber otra página.
  const total = (pagina - 1) * TAREAS_POR_PAGINA + tareas.length + (otra ? 1 : 0);

  if (!configurado)
    return (
      <Alert
        type="info"
        showIcon
        message="Conversaciones no está configurado en este ambiente"
      />
    );

  return (
    <Flex vertical gap={12}>
      <Flex gap={8} align="center" wrap>
        <Select<AgentTaskStatus>
          allowClear
          placeholder="Todos los estados"
          value={estado ?? undefined}
          options={FILTRO_DE_ESTADO}
          onChange={(v) => {
            setEstado(v ?? null);
            setPagina(1);
          }}
          style={{ minWidth: 200 }}
        />
        <Button
          icon={<ReloadOutlined />}
          loading={cargando}
          onClick={() => setRecarga((n) => n + 1)}
        >
          Actualizar
        </Button>
      </Flex>

      {error && (
        <Alert
          type="error"
          showIcon
          message="No se pudieron leer las tareas"
          description={error}
        />
      )}

      <Table
        size="small"
        loading={cargando}
        pagination={{
          current: pagina,
          pageSize: TAREAS_POR_PAGINA,
          total,
          simple: true,
          hideOnSinglePage: true,
          onChange: setPagina,
        }}
        scroll={{ x: true }}
        rowKey="key"
        dataSource={filas}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                estado
                  ? "No hay tareas con ese estado"
                  : "Todavía no le programaron ninguna tarea"
              }
            />
          ),
        }}
        columns={[
          {
            title: "Hora",
            render: (_, f) => (
              <Text style={{ whiteSpace: "nowrap" }}>{f.fecha}</Text>
            ),
          },
          {
            title: "Estado",
            render: (_, f) => (
              <Tag color={f.estado.color} style={{ margin: 0 }}>
                {f.estado.texto}
              </Tag>
            ),
          },
          {
            title: "Dónde",
            render: (_, f) =>
              f.donde.href ? (
                <Link
                  href={f.donde.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {f.donde.nombre}
                </Link>
              ) : (
                <Text type="secondary">{f.donde.nombre}</Text>
              ),
          },
          { title: "La pidió", render: (_, f) => f.pidio },
          {
            title: "",
            render: (_, f) =>
              f.tocable ? (
                <Space size={4}>
                  <CambiarHora
                    runAt={f.runAt}
                    onGuardar={(runAt) => cambiarHora(f.key, runAt)}
                  >
                    <Button size="small">Cambiar hora</Button>
                  </CambiarHora>
                  <Popconfirm
                    title="¿Cancelar la tarea?"
                    description="A esa hora el asistente no va a hacer nada."
                    okText="Cancelar tarea"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => cancelar(f.key)}
                  >
                    <Button
                      size="small"
                      danger
                      type="text"
                      loading={cancelando === f.key}
                    >
                      Cancelar
                    </Button>
                  </Popconfirm>
                </Space>
              ) : null,
          },
        ]}
      />
    </Flex>
  );
};
